import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { MuiMenuItem } from '@fox/atoms/src/mui-list/index';
import { Select } from '@fox/atoms/src/select/index';
import { useDebounce } from '@fox/atoms/hooks/use-debounce';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { FormRenderMode } from '../../../form-runtime-context/contracts';
import { useFormRuntimeContext } from '../../../form-runtime-context/form-runtime-context';
import { updateAnswerForContact } from '../util';
import { CustomFieldLinkAnswer, IAdditionalProfileFieldChoice, SwiftFoxEntity } from './contracts';

export const CustomFieldLink = (props: {
	field: IAdditionalProfileFieldChoice;
	currentAnswerSelections: { contacts: CustomFieldLinkAnswer[] };
	currentAnswer: { questionName: string; answer: { answerJson: string } };
	contactId: string;
	id: string;
	questionType: string;
}) => {
	const { field, currentAnswer, currentAnswerSelections, contactId, id, questionType } = props;
	const { answerQuestion } = useFormRuntimeContext();
	const answer: CustomFieldLinkAnswer = contactId ? currentAnswerSelections?.contacts?.find(c => c.id === contactId) : undefined;
	const handleChange = v => {
		updateAnswerForContact(currentAnswer, currentAnswerSelections, contactId, answerQuestion, field.id, v, questionType);
	};
	const value = field.singleSelect ? (answer?.[field.id] ?? null) : (answer?.[field.id] ?? []);
	return <CustomFieldLinkRender onChange={handleChange} field={field} value={value} multiple={!field.singleSelect} />;
};

export const CustomFieldLinkRender = ({
	field,
	value,
	onChange,
	label,
	multiple,
	helperText
}: {
	field: IAdditionalProfileFieldChoice;
	value;
	onChange: (value) => void;
	label?: string;
	multiple: boolean;
	helperText?: string;
}) => {
	const { formRenderMode, inLocalSurveyMode, complete } = useFormRuntimeContext();
	const [search, setSearch] = useState<string>('');
	const debouncedSearch = useDebounce(search, 1000);
	const token = (window as any).validatedFormToken;
	const { data: completeData, isError } = useQuery({
		queryKey: ['form-link-complete', debouncedSearch, field],
		queryFn: async () => {
			const result = await complete({
				searchString: debouncedSearch,
				types: field.types,
				categories: field.categories,
				signedParams: field.signedParams
			});
			return result ?? [];
		},
		enabled: formRenderMode !== FormRenderMode.design && (!!token || inLocalSurveyMode)
	});
	const options = completeData
		? completeData.map(d => {
				return {
					entity: { id: d.entity.id, type: d.entity.type },
					name: d.name
				};
			})
		: [];
	if (isError) return <MuiAlert severity="error">Unable to load options</MuiAlert>;
	return (
		<Select<SelectOptions, true | false, false, false>
			multiple={multiple}
			disabled={formRenderMode === FormRenderMode.design}
			size="small"
			helperText={helperText}
			options={options}
			renderOption={(props, option) => (
				<MuiMenuItem {...props} key={option.entity?.id ?? option.name} value={option?.entity?.id}>
					{option?.name}
				</MuiMenuItem>
			)}
			label={label}
			limitTags={3}
			value={value}
			getOptionLabel={(o: SelectOptions) => o?.name}
			inputValue={search}
			onInputChange={(_, v) => {
				setSearch(v);
			}}
			isOptionEqualToValue={(option, v) => {
				return v.entity.id === option.entity?.id && v.entity?.type === option.entity?.type;
			}}
			onChange={(_, v) => {
				onChange(v);
			}}
		/>
	);
};

type SelectOptions = {
	entity: SwiftFoxEntity;
	name: string;
};
